import { useCallback, useMemo, useState } from 'react'
import type {
  AgentMode,
  ClaudeSession,
  ConversationTurn,
  ConversationTurnEntry,
  UserOption,
} from '@/index'
import { useDialog } from '@/common/hooks'
import { deriveConversationControlsState } from '../utils'
import type { ConversationControlsState } from '../types'
import type { UseCommandSubmitterReturn } from './useCommandSubmitter.ts'

const SYNTHETIC_CUSTOM_INPUT_INDEX = 3
const SYNTHETIC_LABELS: Record<number, string> = {
  1: 'Allow',
  2: 'Always allow',
  3: 'Something else',
}

interface UseUserCommandDialogsParams {
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly submitter: UseCommandSubmitterReturn
  readonly currentAgentMode: AgentMode
}

interface UseUserCommandDialogsReturn {
  readonly inject: {
    readonly isOpen: boolean
    readonly open: () => void
    readonly close: () => void
    readonly handleSubmit: (prompt: string, agentMode?: AgentMode | null) => Promise<boolean>
  }

  readonly reply: {
    readonly isOpen: boolean
    readonly open: () => void
    readonly close: () => void
    readonly handleSubmit: (prompt: string, agentMode?: AgentMode | null) => Promise<boolean>
  }

  readonly userOption: {
    readonly isOpen: boolean
    readonly selectedOption: UserOption | null
    readonly selectedEvent: ConversationTurnEntry | null
    readonly handleSelect: (optionIndex: number, event: ConversationTurnEntry) => void
    readonly handleSubmit: (customText: string, agentMode?: AgentMode | null) => Promise<boolean>
    readonly close: () => void
  }
  readonly controls: ConversationControlsState

  readonly handlePause: () => Promise<void>

  readonly handleResume: () => Promise<void>
}

export function useUserCommandDialogs(
  params: UseUserCommandDialogsParams,
): UseUserCommandDialogsReturn {
  const { conversationTurn, submitter } = params

  const injectDialog = useDialog()
  const replyDialog = useDialog()
  const userOptionDialog = useDialog()

  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<ConversationTurnEntry | null>(null)

  const controls = useMemo(
    () => deriveConversationControlsState(conversationTurn ?? undefined),
    [conversationTurn],
  )

  const handleInjectSubmit = useCallback(
    async (prompt: string, agentMode?: AgentMode | null): Promise<boolean> => {
      try {
        await submitter.submitInject(prompt, agentMode ?? undefined)
        injectDialog.close()
        return true
      } catch {
        return false
      }
    },
    [submitter, injectDialog],
  )

  const handleReplySubmit = useCallback(
    async (prompt: string, agentMode?: AgentMode | null): Promise<boolean> => {
      try {
        await submitter.submitReply(prompt, agentMode ?? undefined)
        replyDialog.close()
        return true
      } catch {
        return false
      }
    },
    [submitter, replyDialog],
  )

  const handleUserOptionSelect = useCallback(
    (optionIndex: number, event: ConversationTurnEntry): void => {
      const agentDialogue = event.notificationContext?.agentDialogue
      const realOption = agentDialogue?.userOptions?.find(
        (opt: UserOption) => opt.index === optionIndex,
      )

      if (realOption && agentDialogue?.userOptions) {
        if (requiresCustomInput(realOption, agentDialogue.userOptions)) {
          setSelectedOption(realOption)
          setSelectedEvent(event)
          userOptionDialog.open()
        } else {
          submitter.submitUserOption(event, realOption, null).catch(() => {})
        }
        return
      }

      const syntheticOption: UserOption = {
        index: optionIndex,
        option: SYNTHETIC_LABELS[optionIndex] ?? String(optionIndex),
      }
      if (optionIndex === SYNTHETIC_CUSTOM_INPUT_INDEX) {
        setSelectedOption(syntheticOption)
        setSelectedEvent(event)
        userOptionDialog.open()
      } else {
        submitter.submitUserOption(event, syntheticOption, null).catch(() => {})
      }
    },
    [submitter, userOptionDialog],
  )

  const handleUserOptionSubmit = useCallback(
    async (customText: string, agentMode?: AgentMode | null): Promise<boolean> => {
      if (!selectedOption || !selectedEvent) return false

      try {
        await submitter.submitUserOption(
          selectedEvent,
          selectedOption,
          customText,
          agentMode ?? undefined,
        )
        setSelectedOption(null)
        setSelectedEvent(null)
        userOptionDialog.close()
        return true
      } catch {
        return false
      }
    },
    [selectedOption, selectedEvent, submitter, userOptionDialog],
  )

  const handleUserOptionClose = useCallback((): void => {
    setSelectedOption(null)
    setSelectedEvent(null)
    userOptionDialog.close()
  }, [userOptionDialog])

  const handlePause = useCallback(async (): Promise<void> => {
    try {
      await submitter.submitPause()
    } catch {
      // Error already handled by submitter (sets error state + calls onError)
    }
  }, [submitter])

  const handleResume = useCallback(async (): Promise<void> => {
    try {
      await submitter.submitResume()
    } catch {
      // Error already handled by submitter (sets error state + calls onError)
    }
  }, [submitter])

  return {
    inject: {
      isOpen: injectDialog.isOpen,
      open: injectDialog.open,
      close: injectDialog.close,
      handleSubmit: handleInjectSubmit,
    },
    reply: {
      isOpen: replyDialog.isOpen,
      open: replyDialog.open,
      close: replyDialog.close,
      handleSubmit: handleReplySubmit,
    },
    userOption: {
      isOpen: userOptionDialog.isOpen,
      selectedOption,
      selectedEvent,
      handleSelect: handleUserOptionSelect,
      handleSubmit: handleUserOptionSubmit,
      close: handleUserOptionClose,
    },
    handlePause,
    handleResume,
    controls,
  }
}

function requiresCustomInput(option: UserOption, allOptions: readonly UserOption[]): boolean {
  if (allOptions.length === 0) return false
  const maxIndex = Math.max(...allOptions.map((opt) => opt.index))
  return option.index === maxIndex
}
